import type {
  BlueprintConnection,
  BlueprintDocument,
  BlueprintResource,
  ConnectionTargetInfo,
  DeploymentSettings,
  JsonValue,
  Provider,
  ResourceCondition,
  ResourceType,
  ServiceNameInfo,
  TriggerSourceInfo,
} from '../api/types';
import { strings } from '../strings';
import { parsePointer } from './jsonPointer';

// Provider descriptions (feature spec 16, 9.3; spec 11 6.3.10): which provider's trigger source, connection target
// or service name entry describes a resource. Pure data from `GET /providers`; no type or provider names here.

/** The deployment selected in the overlay, as far as provider resolution needs it (step 1). */
export interface OverlayPlacement {
  name: string;
  settings: Pick<DeploymentSettings, 'defaultPlacement' | 'placementOverrides'>;
}

export interface DescriptionContext {
  doc: BlueprintDocument;
  types: ReadonlyMap<string, ResourceType>;
  providers: readonly Provider[];
  overlay: OverlayPlacement | null;
}

export type Provenance =
  | { step: 'overlay'; provider: Provider; deployment: string; resourceId: string }
  | { step: 'placement'; provider: Provider; resourceId: string }
  | { step: 'supported'; providers: Provider[]; typeName: string };

export interface Described<T> {
  info: T;
  provenance: Provenance;
}

/** A value with `${` isn't a literal in the document (the server decides after resolution). */
function literalText(value: JsonValue | undefined): string | null {
  if (value === undefined) return null;
  if (typeof value === 'string') return value.includes('${') ? null : value;
  return JSON.stringify(value);
}

/** The value at a pointer into the blueprint resource object (`/properties/…`, `/providerOverrides/<key>/…`). */
export function resourceValue(
  resource: BlueprintResource,
  pointer: string,
  providerKey: string | null | undefined,
): JsonValue | undefined {
  const segments = parsePointer(pointer);
  let current: JsonValue | undefined;
  let path: string[];
  if (segments[0] === 'properties') {
    current = resource.properties;
    path = segments.slice(1);
  } else if (segments[0] === 'providerOverrides') {
    const key = segments[1];
    if (!key || key !== providerKey) return undefined;
    current = resource.providerOverrides?.[key];
    path = segments.slice(2);
  } else return undefined;
  for (const segment of path) {
    if (current === null || typeof current !== 'object') return undefined;
    current = Array.isArray(current) ? current[Number(segment)] : current[segment];
  }
  return current;
}

/**
 * The client mirror of the server's `ResourceConditions` (feature spec 16, 4.3): the literal value at `pointer`
 * equals `value` (non-strings by their JSON text); `/providerOverrides/<key>/…` only for the resource's provider;
 * a missing value or one with `${` never matches.
 */
export function matchesCondition(
  condition: ResourceCondition,
  resource: BlueprintResource,
  providerKey: string | null | undefined,
): boolean {
  const text = literalText(resourceValue(resource, condition.pointer, providerKey));
  return text !== null && text === condition.value;
}

/** Within one provider's list: the first entry for the type whose `when` matches, else the default entry. */
export function pickEntry<T extends { when?: ResourceCondition | null }>(
  entries: readonly T[] | null | undefined,
  typeOf: (entry: T) => string,
  resource: BlueprintResource,
  providerKey: string,
): T | undefined {
  const own = (entries ?? []).filter((e) => typeOf(e) === resource.type);
  return (
    own.find((e) => e.when && matchesCondition(e.when, resource, providerKey)) ?? own.find((e) => !e.when)
  );
}

/** The resource's known provider: the overlay's effective one (step 1), else its placement (step 2). */
export function knownProvider(
  ctx: Pick<DescriptionContext, 'providers' | 'overlay'>,
  resource: BlueprintResource,
): { provider: Provider; step: 'overlay' | 'placement' } | { key: string; step: 'unknown-provider' } | null {
  const fromOverlay = ctx.overlay
    ? ctx.overlay.settings.placementOverrides[resource.id]?.provider ||
      resource.placement?.provider ||
      ctx.overlay.settings.defaultPlacement?.provider
    : undefined;
  const key = fromOverlay || resource.placement?.provider;
  if (!key) return null;
  const provider = ctx.providers.find((p) => p.key === key);
  if (!provider) return { key, step: 'unknown-provider' };
  return { provider, step: fromOverlay ? 'overlay' : 'placement' };
}

function describe<T extends { when?: ResourceCondition | null }>(
  ctx: DescriptionContext,
  resource: BlueprintResource,
  otherType: string | undefined,
  list: (provider: Provider) => readonly T[] | null | undefined,
  typeOf: (entry: T) => string,
  same: (a: T, b: T) => boolean,
): Described<T> | null {
  const known = knownProvider(ctx, resource);
  if (known) {
    if (known.step === 'unknown-provider') return null;
    const info = pickEntry(list(known.provider), typeOf, resource, known.provider.key);
    if (!info) return null;
    return {
      info,
      provenance:
        known.step === 'overlay'
          ? {
              step: 'overlay',
              provider: known.provider,
              deployment: ctx.overlay!.name,
              resourceId: resource.id,
            }
          : { step: 'placement', provider: known.provider, resourceId: resource.id },
    };
  }
  // Step 3: every provider that supports both types describes it the same way.
  const supporting = ctx.providers.filter(
    (p) => p.supportedTypes.includes(resource.type) && (!otherType || p.supportedTypes.includes(otherType)),
  );
  if (supporting.length === 0) return null;
  const entries = supporting.map((p) => pickEntry(list(p), typeOf, resource, p.key));
  const first = entries[0];
  if (!first || entries.some((e) => !e || !same(first, e))) return null;
  return {
    info: first,
    provenance: {
      step: 'supported',
      providers: supporting,
      typeName: ctx.types.get(resource.type)?.displayName ?? resource.type,
    },
  };
}

function resourceById(doc: BlueprintDocument, id: string): BlueprintResource | undefined {
  return doc.resources.find((r) => r.id === id);
}

const sameSuffixes = (a: TriggerSourceInfo, b: TriggerSourceInfo) =>
  a.settings.map((s) => s.suffix).join('\n') === b.settings.map((s) => s.suffix).join('\n');

const sameTarget = (a: ConnectionTargetInfo, b: ConnectionTargetInfo) =>
  a.roles.join('\n') === b.roles.join('\n') && (a.note ?? '') === (b.note ?? '');

/** The trigger source description of a `triggers` connection (resolved for its source). */
export function resolveTriggerSource(
  ctx: DescriptionContext,
  connection: Pick<BlueprintConnection, 'sourceId' | 'targetId'>,
): Described<TriggerSourceInfo> | null {
  const source = resourceById(ctx.doc, connection.sourceId);
  if (!source) return null;
  const target = resourceById(ctx.doc, connection.targetId);
  return describe(
    ctx,
    source,
    target?.type,
    (p) => p.triggerSources,
    (e) => e.sourceType,
    sameSuffixes,
  );
}

/** The connection target description of a `connects-to` connection (resolved for its target). */
export function resolveConnectionTarget(
  ctx: DescriptionContext,
  connection: Pick<BlueprintConnection, 'sourceId' | 'targetId'>,
): Described<ConnectionTargetInfo> | null {
  const target = resourceById(ctx.doc, connection.targetId);
  if (!target) return null;
  const source = resourceById(ctx.doc, connection.sourceId);
  return describe(
    ctx,
    target,
    source?.type,
    (p) => p.connectionTargets,
    (e) => e.targetType,
    sameTarget,
  );
}

/** The native service a resource becomes, only with a known provider (feature spec 16, 9.1). */
export function resolveServiceName(
  ctx: Pick<DescriptionContext, 'providers' | 'overlay'>,
  resource: BlueprintResource,
): { name: string; provider: Provider } | null {
  const known = knownProvider(ctx, resource);
  if (!known || known.step === 'unknown-provider') return null;
  const entry = pickEntry<ServiceNameInfo>(
    known.provider.serviceNames,
    (e) => e.type,
    resource,
    known.provider.key,
  );
  return entry ? { name: entry.name, provider: known.provider } : null;
}

/** Palette lines: one per supporting provider with its service names (default entry first, distinct). */
export function serviceNameLines(providers: readonly Provider[], type: string): string[] {
  const lines: string[] = [];
  for (const provider of providers) {
    if (!provider.supportedTypes.includes(type)) continue;
    const entries = (provider.serviceNames ?? []).filter((e) => e.type === type);
    const ordered = [...entries.filter((e) => !e.when), ...entries.filter((e) => e.when)];
    const names = [...new Set(ordered.map((e) => e.name))];
    if (names.length) lines.push(strings.descriptions.serviceLine(provider.displayName, names.join(', ')));
  }
  return lines;
}

export function provenanceText(provenance: Provenance): string {
  const d = strings.descriptions;
  switch (provenance.step) {
    case 'overlay':
      return d.fromOverlay(provenance.provider.displayName, provenance.deployment, provenance.resourceId);
    case 'placement':
      return d.fromPlacement(provenance.provider.displayName, provenance.resourceId);
    default:
      return provenance.providers.length === 1
        ? d.fromOnly(provenance.providers[0].displayName, provenance.typeName)
        : d.fromAgreeing(provenance.providers.map((p) => p.displayName).join(', '), provenance.typeName);
  }
}

/** The settings a triggered function receives: `<Alias><suffix>`. */
export function triggerSettingNames(info: TriggerSourceInfo, alias: string): string[] {
  return info.settings.map((s) => `${alias}${s.suffix}`);
}

/** The access line of a trigger description (feature spec 16, 7.15.1). */
export function triggerAccess(info: TriggerSourceInfo, sourceId: string): string {
  const d = strings.descriptions;
  if (info.roles.length > 0)
    return info.functionEffect ? `${info.roles.join(', ')}. ${info.functionEffect}` : info.roles.join(', ');
  if (info.functionEffect) return info.functionEffect;
  return d.noRole(sourceId);
}

/** The third part of a collapsed card's summary. */
export function triggerAccessSummary(info: TriggerSourceInfo): string {
  if (info.roles.length > 0) return info.roles.join(', ');
  return info.functionEffect ? strings.descriptions.changesApp : strings.descriptions.noRoleShort;
}
